import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, AlertCircle, CheckCircle2, FileCheck2, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button, Card, Field, Input, Select, Textarea, Spinner } from "@/components/ui";
import type { Json } from "@/lib/database.types";
import { expiryBand } from "@/features/licences/licenceHelpers";

// -----------------------------------------------------------------------------
// T-002 — /profile edit surface
// Self-service edit of the logged-in user's employee record. Reuses the same
// field layout as the onboarding wizard but flat (no stepper). Writes via
// `app.update_employee_profile` — COALESCE-on-optional, direct-assign on
// anything required. Server enforces role gates (employment fields require
// manager+, payroll requires director); the UI also grays those out for
// non-authorised callers so it's obvious before they try.
// -----------------------------------------------------------------------------

const AU_STATES = ["QLD", "NSW", "VIC", "TAS", "SA", "WA", "NT", "ACT"] as const;

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] as const;

// Every editable key on the employees row this screen touches. Mirrors the
// wizard's OnboardingDraft plus the subset of employment/payroll fields the
// update RPC handles. Keep in sync when new fields are added to employees.
interface ProfileDraft {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  preferred_name?: string;
  date_of_birth?: string;
  mobile?: string;
  email?: string;
  home_phone?: string;
  address_line1?: string;
  address_line2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  next_of_kin_name?: string;
  next_of_kin_phone?: string;
  next_of_kin_relationship?: string;
  blood_type?: string;
  emergency_medical_notes?: string;
}

// Shape returned by app.get_my_onboarding_context. Reused here for edit-
// after-onboarding context loading — the RPC returns employee + flags for
// either wizard or edit mode. Name mismatch is cosmetic; the data is right.
interface OnboardingContext {
  user: {
    user_id: string;
    auth_user_id: string;
    login_email: string;
    role_name: string;
    employee_id: string | null;
    onboarding_completed_at: string | null;
  } | null;
  employee: Record<string, unknown> | null;
  auth_email: string | null;
  is_director: boolean;
  is_self: boolean;
}

export default function ProfileScreen() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ProfileDraft>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Load context (same RPC the wizard uses — returns user + employee + flags)
  // ---------------------------------------------------------------------------
  const { data: context, isLoading, error } = useQuery({
    queryKey: ["profile_context"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("app")
        .rpc("get_my_onboarding_context", { p_target_user_id: undefined });
      if (error) throw error;
      return data as unknown as OnboardingContext;
    },
  });

  // Scope note: this self-edit surface deliberately excludes employment fields
  // (start_date, employment_type, primary_role_name) and payroll fields (bank,
  // super, LAFHA). Employment lives next to leave balance in the Leave portal
  // (T-007); payroll lives in director-only Finance admin (T-035). Per James
  // 2026-04-23 — /profile is only personal contact + emergency medical.

  // ---------------------------------------------------------------------------
  // Save mutation
  // ---------------------------------------------------------------------------
  const mutation = useMutation({
    mutationFn: async () => {
      const employeeId = context?.user?.employee_id;
      if (!employeeId) {
        throw new Error(
          "No employee record linked to your login. Ask a director to complete setup.",
        );
      }
      const { data, error } = await supabase
        .schema("app")
        .rpc("update_employee_profile", {
          p_employee_id: employeeId,
          p_payload: draft as unknown as Json,
        });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setDraft({});
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ["profile_context"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding_context"] });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error
          ? err.message
          : (err as { message?: string })?.message ?? "Save failed — please try again.";
      setSaveError(msg);
    },
  });

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (error || !context?.user) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 flex items-start gap-3">
            <AlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Can't load your profile</div>
              <div className="text-sm text-text-muted">
                {error instanceof Error
                  ? error.message
                  : "No user record found for this login."}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!context.employee) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 flex items-start gap-3">
            <AlertCircle size={18} className="text-warn flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">No employee record yet</div>
              <div className="text-sm text-text-muted">
                Complete onboarding first — visit the Dashboard and you'll be
                redirected.
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Value + change helpers — prefer draft, fall back to employee row
  // ---------------------------------------------------------------------------
  const emp = context.employee;

  const v = (key: keyof ProfileDraft): string => {
    const d = draft[key];
    if (typeof d === "string") return d;
    const e = emp[key as string];
    return typeof e === "string" ? e : "";
  };

  const bool = (key: keyof ProfileDraft): boolean => {
    const d = draft[key];
    if (typeof d === "boolean") return d;
    const e = emp[key as string];
    return typeof e === "boolean" ? e : false;
  };

  const onField = <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    if (saveError) setSaveError(null);
  };

  const changeCount = Object.keys(draft).length;
  const hasChanges = changeCount > 0;
  const isSaving = mutation.isPending;
  const justSaved = mutation.isSuccess && !hasChanges;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="p-6 max-w-4xl mx-auto pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">My profile</h1>
        <p className="text-sm text-text-muted mt-1">
          Your personal details, emergency contacts, and employment info.
          Changes save directly — no wizard to walk.
        </p>
      </div>

      {/* Personal details */}
      <ProfileSection title="Personal details">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <Field label="First name">
            <Input
              type="text"
              value={v("first_name")}
              onChange={(e) => onField("first_name", e.target.value)}
            />
          </Field>
          <Field label="Middle name">
            <Input
              type="text"
              value={v("middle_name")}
              onChange={(e) => onField("middle_name", e.target.value)}
            />
          </Field>
          <Field label="Last name">
            <Input
              type="text"
              value={v("last_name")}
              onChange={(e) => onField("last_name", e.target.value)}
            />
          </Field>
          <Field
            label="Preferred name"
            hint="what you go by if different from first name"
          >
            <Input
              type="text"
              value={v("preferred_name")}
              onChange={(e) => onField("preferred_name", e.target.value)}
            />
          </Field>
          <Field label="Date of birth">
            <Input
              type="date"
              value={v("date_of_birth")}
              onChange={(e) => onField("date_of_birth", e.target.value)}
            />
          </Field>
          <Field label="Mobile">
            <Input
              type="tel"
              inputMode="numeric"
              value={v("mobile")}
              onChange={(e) => onField("mobile", e.target.value)}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={v("email")}
              onChange={(e) => onField("email", e.target.value)}
            />
          </Field>
          <Field label="Home phone">
            <Input
              type="tel"
              inputMode="numeric"
              value={v("home_phone")}
              onChange={(e) => onField("home_phone", e.target.value)}
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Address line 1">
              <Input
                type="text"
                value={v("address_line1")}
                onChange={(e) => onField("address_line1", e.target.value)}
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Address line 2">
              <Input
                type="text"
                value={v("address_line2")}
                onChange={(e) => onField("address_line2", e.target.value)}
              />
            </Field>
          </div>
          <Field label="Suburb">
            <Input
              type="text"
              value={v("suburb")}
              onChange={(e) => onField("suburb", e.target.value)}
            />
          </Field>
          <Field label="State">
            <Select
              value={v("state")}
              onChange={(e) => onField("state", e.target.value)}
            >
              <option value="">—</option>
              {AU_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Postcode">
            <Input
              type="text"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              value={v("postcode")}
              onChange={(e) => onField("postcode", e.target.value)}
            />
          </Field>
        </div>
      </ProfileSection>

      {/* Next of kin */}
      <ProfileSection title="Next of kin">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <Field label="Name">
            <Input
              type="text"
              value={v("next_of_kin_name")}
              onChange={(e) => onField("next_of_kin_name", e.target.value)}
            />
          </Field>
          <Field label="Phone">
            <Input
              type="tel"
              inputMode="numeric"
              value={v("next_of_kin_phone")}
              onChange={(e) => onField("next_of_kin_phone", e.target.value)}
            />
          </Field>
          <div className="md:col-span-2">
            <Field
              label="Relationship"
              hint="e.g. spouse, parent, sibling"
            >
              <Input
                type="text"
                value={v("next_of_kin_relationship")}
                onChange={(e) =>
                  onField("next_of_kin_relationship", e.target.value)
                }
              />
            </Field>
          </div>
        </div>
      </ProfileSection>

      {/* Emergency medical */}
      <ProfileSection
        title="Emergency medical"
        hint="Optional. Shared only with first-aid responders."
      >
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <Field label="Blood type">
            <Select
              value={v("blood_type")}
              onChange={(e) => onField("blood_type", e.target.value)}
            >
              <option value="">—</option>
              {BLOOD_TYPES.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Select>
          </Field>
          <div className="md:col-span-2">
            <Field
              label="Medical notes"
              hint="allergies, medications, conditions responders should know"
            >
              <Textarea
                rows={3}
                value={v("emergency_medical_notes")}
                onChange={(e) =>
                  onField("emergency_medical_notes", e.target.value)
                }
              />
            </Field>
          </div>
        </div>
      </ProfileSection>

      {/* Licences summary (T-004 integration) */}
      <LicencesSummaryCard employeeId={context.user?.employee_id ?? null} />

      {/* Sticky save bar */}
      <div className="sticky bottom-4 max-md:bottom-[calc(var(--nav-clear)+1rem)] mt-6 z-10">
        <Card className="shadow-lg">
          <div className="p-4 flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              {saveError ? (
                <div className="flex items-center gap-2 text-sm text-danger">
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <span className="truncate">{saveError}</span>
                </div>
              ) : justSaved ? (
                <div className="flex items-center gap-2 text-sm text-ok">
                  <CheckCircle2 size={14} />
                  <span>Saved.</span>
                </div>
              ) : hasChanges ? (
                <div className="text-sm text-text-muted">
                  {changeCount} unsaved change{changeCount !== 1 ? "s" : ""}.
                </div>
              ) : (
                <div className="text-sm text-text-muted">No changes.</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <Button
                  variant="ghost"
                  disabled={isSaving}
                  onClick={() => {
                    setDraft({});
                    setSaveError(null);
                  }}
                >
                  Discard
                </Button>
              )}
              <Button
                variant="primary"
                icon={Save}
                disabled={!hasChanges || isSaving}
                onClick={() => {
                  setSaveError(null);
                  mutation.mutate();
                }}
              >
                {isSaving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  hint?: string;
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Licences summary card — at-a-glance on /profile, full view on /licences.
// ---------------------------------------------------------------------------

interface LicencesSummaryCardProps {
  employeeId: string | null;
}

function LicencesSummaryCard({ employeeId }: LicencesSummaryCardProps) {
  const { data } = useQuery({
    queryKey: ["my_licences_summary", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select("licence_id, expiry_date, verified_at, licence_type:licence_types(requires_verification)")
        .eq("employee_id", employeeId!)
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? []) as Array<{
        licence_id: string;
        expiry_date: string | null;
        verified_at: string | null;
        licence_type: { requires_verification: boolean } | null;
      }>;
    },
  });

  const total    = data?.length ?? 0;
  const overdue  = data?.filter(l => expiryBand(l.expiry_date) === "overdue").length ?? 0;
  const warning  = data?.filter(l => expiryBand(l.expiry_date) === "warning").length ?? 0;
  const pending  = data?.filter(l => l.licence_type?.requires_verification && !l.verified_at).length ?? 0;

  return (
    <Card className="mb-4">
      <Link
        to="/licences"
        className="block p-6 hover:bg-ink-hover transition-colors rounded"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-accent/12 text-accent flex items-center justify-center flex-shrink-0">
              <FileCheck2 size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2">
                Licences & inductions
              </div>
              <div className="text-sm mt-1">
                {total === 0 ? (
                  <span className="text-text-muted">None on record yet — tap to add your first</span>
                ) : (
                  <span>
                    <span className="font-medium">{total}</span>
                    <span className="text-text-muted"> on record</span>
                    {overdue > 0 && (
                      <span className="text-danger ml-2">· {overdue} overdue</span>
                    )}
                    {warning > 0 && (
                      <span className="text-warn ml-2">· {warning} expiring soon</span>
                    )}
                    {pending > 0 && (
                      <span className="text-text-muted ml-2">· {pending} awaiting verification</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
          <ChevronRight size={18} className="text-text-muted flex-shrink-0" />
        </div>
      </Link>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function ProfileSection({ title, hint, children }: SectionProps) {
  return (
    <Card className="mb-4">
      <div className="p-6">
        <div className="mb-4">
          <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2">
            {title}
          </h2>
          {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
        </div>
        {children}
      </div>
    </Card>
  );
}
